<template>
  <div v-if="linkData.url !== '' && linkData.transitionCase === 'routerLink'">
    <router-link class="link-item-parts" :to="linkData.url">
      <i class="link-item-parts__icon"><img :src="linkData.iconPath" alt="" /></i>
      <span class="link-item-parts__text" v-html="linkData.text" />
    </router-link>
  </div>
  <div v-else-if="linkData.url !== '' && linkData.transitionCase === 'inAppBrowser'">
    <a class="link-item-parts" href="javascript:void(0)" @click="onClickAnchorLink(linkData.url)">
      <i class="link-item-parts__icon"><img :src="linkData.iconPath" alt="" /></i>
      <span class="link-item-parts__text" v-html="linkData.text" />
    </a>
  </div>
  <div v-else-if="linkData.url !== ''">
    <a class="link-item-parts" :href="linkData.url" target="_blank">
      <i class="link-item-parts__icon"><img :src="linkData.iconPath" alt="" /></i>
      <span class="link-item-parts__text" v-html="linkData.text" />
    </a>
  </div>
  <div v-else class="link-item-parts">
    <i class="link-item-parts__icon"><img :src="linkData.iconPath" alt="" /></i>
    <span class="link-item-parts__text" v-html="linkData.text" />
  </div>
</template>

<script lang="ts">
import { defineComponent, PropType } from '@vue/composition-api'
import { LinkData } from '@/components/LinkPage/LinkPane.vue'
import InAppBrowserWrapper, { InAppBrowserResult } from '@/util/inAppBrowser/InAppBrowserWrapper'
import MessageDialogStore from '@/store/stores/pageStore/common/MessageDialogStore'
import I18n from '@/locales/I18n'
import LoginStore from '@/store/stores/loginStore/LoginStore'
import UserStore from '@/store/stores/pageStore/common/UserStore'
import LocalStorageAccessor from '@/util/localstorage/LocalStorageAccessor'

/**
 * Link画面から遷移するパターン
 */
export type LinkTransitionType = 'routerLink' | 'inAppBrowser' | 'externalLink'

/**
 * Link アイテム
 */
export default defineComponent({
  name: 'LinkItemParts',
  components: {},
  props: {
    linkData: {
      type: Object as PropType<LinkData>,
      required: true,
    },
  },
  setup() {
    const inAppBrowser = new InAppBrowserWrapper()

    /**
     * inAppBrowserで開くLINKカードをクリックした場合に呼び出される。
     * @param url 外部サイトのリンク
     */
    const onClickAnchorLink = (url: string) => {
      if (!InAppBrowserWrapper.isAvailable()) {
        // ブラウザの場合、別ウィンドウか別タブで表示する
        const subWindow = window.open(url, '_blank')
        if (subWindow && process.env.VUE_APP_SFBINGO_URL === url) {
          // URLがSFbingoの場合には、APIの認証情報とユーザーIDをローカルストレージに保存する
          LocalStorageAccessor.setCredentialInfo(
            UserStore.value.user.value._organization || '',
            UserStore.value.user.value.id || '',
            LoginStore.value.accessToken || '',
            LoginStore.value.refreshToken || '',
          )
          setTimeout(() => {
            LocalStorageAccessor.clearCredentialInfo()
          }, 60000)
        }
        return
      }

      let injectCode = ''
      if (url === process.env.VUE_APP_SFBINGO_URL) {
        const credentialInfo = {
          organizationId: UserStore.value.user.value._organization,
          userId: UserStore.value.user.value.id,
          accessToken: LoginStore.value.accessToken,
          refreshToken: LoginStore.value.refreshToken,
        }
        const sCredentialInfo = JSON.stringify(credentialInfo)
        injectCode = `window.onLoadCredentials('${sCredentialInfo}');`
      }

      // InAppBrowserでサイトを開く
      inAppBrowser.open(
        url,
        '_blank',
        undefined,
        async (result: InAppBrowserResult) => {
          if (result?.isNetworkError) {
            await MessageDialogStore.value.open({
              title: I18n.t('apiNotification.dialog.0.title') as string,
              message: I18n.t('apiNotification.dialog.0.message') as string,
            })
          }
        },
        false,
        injectCode,
      )
    }

    return {
      onClickAnchorLink,
    }
  },
})
</script>

<style lang="scss" scoped>
.link-item-parts {
  display: flex;
  flex-flow: column nowrap;
  justify-content: center;
  align-items: center;
  width: min(#{convertPXtoVW(82), 82px});
  padding: min(#{convertPXtoVW(10), 10px}) min(#{convertPXtoVW(6), 6px});
  gap: 4px;
  height: min(#{convertPXtoVW(115), 115px});
  background: $white;
  border: 1px solid rgba(0, 0, 0, 0.1);
  border-radius: 4px;

  &__icon {
    width: 100%;
    text-align: center;

    img {
      width: auto;
      height: min(#{convertPXtoVW(32), 32px});
    }
  }

  &__text {
    font-family: $fontSignup;
    font-weight: 500;
    font-size: min(#{convertPXtoVW(12), 12px});
    line-height: 1.33;
    text-align: center;
    letter-spacing: 0.02em;
    font-feature-settings: 'palt' on;
    color: rgba(0, 0, 0, 0.7);
  }
}
</style>
